import React, { useState, useRef, useEffect, useCallback } from "react";
import { IconButton, Stack, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Minimize";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InfoIcon from "@mui/icons-material/Info";
import {
  WindowOverlay,
  WindowContainer,
  WindowHeader,
  WindowTitle,
  WindowControls,
  WindowContent,
  ResizeHandle,
} from "./styled";

/**
 * Props interface for the ResizableDraggableWindow component.
 *
 * @property open - Controls whether the window is visible
 * @property onClose - Callback invoked when the window should close
 * @property title - Title displayed in the window header (default: "Window")
 * @property children - Content to render inside the window
 * @property initialWidth - Initial window width in pixels (default: 600)
 * @property initialHeight - Initial window height in pixels (default: 400)
 * @property minWidth - Minimum allowed window width in pixels (default: 300)
 * @property minHeight - Minimum allowed window height in pixels (default: 200)
 * @property maxWidth - Maximum allowed window width in pixels (default: screen width - 40)
 * @property maxHeight - Maximum allowed window height in pixels (default: screen height - 40)
 * @property onSizeChange - Callback invoked when window size changes, receives new dimensions
 * @property zIndex - Z-index for window stacking order (default: 1300)
 * @property onClick - Callback invoked when the window is clicked
 * @property titleTooltip - Optional tooltip text to display next to the window title
 */
interface ResizableDraggableWindowProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  initialWidth?: number;
  initialHeight?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  onSizeChange?: (size: { width: number; height: number }) => void;
  zIndex?: number;
  onClick?: () => void;
  /**
   * Tooltip text to display next to the title
   */
  titleTooltip?: string;
}

/**
 * A fully-featured resizable and draggable floating window component.
 *
 * This component provides a modal-like window that can be dragged around the screen and
 * resized from any edge or corner. It includes window controls for minimize, maximize,
 * and close operations. The window can be minimized to the top-left corner or maximized
 * to fill the viewport. Supports both mouse and touch interactions for mobile compatibility.
 *
 * Key features:
 * - Draggable by clicking and dragging the header
 * - Resizable from all 8 directions (4 edges + 4 corners)
 * - Minimize to compact header-only view
 * - Maximize to fill viewport
 * - Boundary constraints to keep window on screen
 * - Touch device support
 * - Optional tooltip next to title
 * - Customizable dimensions and z-index
 *
 * @param props - Component properties
 */
export const ResizableDraggableWindow: React.FC<
  ResizableDraggableWindowProps
> = ({
  open,
  onClose,
  title = "Window",
  children,
  initialWidth = 600,
  initialHeight = 400,
  minWidth = 300,
  minHeight = 200,
  maxWidth = window.innerWidth - 40,
  maxHeight = window.innerHeight - 40,
  onSizeChange,
  zIndex = 1300,
  onClick,
  titleTooltip,
}) => {
  const initialCenterX = Math.max(20, (window.innerWidth - initialWidth) / 2);
  const initialCenterY = Math.max(20, (window.innerHeight - initialHeight) / 2);

  const [position, setPosition] = useState({
    x: initialCenterX,
    y: initialCenterY,
  });
  const [size, setSize] = useState({
    width: initialWidth,
    height: initialHeight,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string>("");
  const [isMinimized, setIsMinimized] = useState(false);
  const [previousHeight, setPreviousHeight] = useState<number>(initialHeight);
  const [previousWidth, setPreviousWidth] = useState<number>(initialWidth);
  const [previousPosition, setPreviousPosition] = useState({
    x: initialCenterX,
    y: initialCenterY,
  });

  const dragStartPos = useRef({ x: 0, y: 0 });
  const resizeStartPos = useRef({ x: 0, y: 0 });
  const resizeStartSize = useRef({ width: 0, height: 0 });
  const resizeStartPosition = useRef({ x: 0, y: 0 });
  const windowRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (open) {
      const centerX = (window.innerWidth - initialWidth) / 2;
      const centerY = (window.innerHeight - initialHeight) / 2;
      setPosition({ x: Math.max(20, centerX), y: Math.max(20, centerY) });
      setSize({ width: initialWidth, height: initialHeight });
      setIsMinimized(false);
    }
  }, [open, initialWidth, initialHeight]);

  /**
   * Initiates window dragging when the header is clicked or touched.
   * Prevents dragging when clicking window controls or when window is minimized.
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if ((e.target as HTMLElement).closest(".window-controls")) return;
      if (isMinimized) return;

      if ("touches" in e) {
        e.preventDefault();
      }

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

      setIsDragging(true);
      dragStartPos.current = {
        x: clientX - position.x,
        y: clientY - position.y,
      };
    },
    [position, isMinimized],
  );

  /**
   * Initiates window resizing from a specific edge or corner.
   * Stops event propagation to prevent triggering drag behavior.
   *
   * @param e - Mouse or touch event
   * @param direction - Resize direction (n, s, e, w, ne, nw, se, sw)
   */
  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent, direction: string) => {
      e.stopPropagation();
      if ("touches" in e) {
        e.preventDefault();
      }

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

      setIsResizing(true);
      setResizeDirection(direction);
      resizeStartPos.current = { x: clientX, y: clientY };
      resizeStartSize.current = { ...size };
      resizeStartPosition.current = { ...position };
    },
    [size, position],
  );

  useEffect(() => {
    /**
     * Handles mouse and touch move events for dragging and resizing the window.
     *
     * When dragging, calculates new window position and constrains it within viewport bounds.
     * When resizing, calculates new dimensions based on resize direction (n, s, e, w, ne, nw, se, sw)
     * and applies min/max constraints. Adjusts position when resizing from top or left edges.
     *
     * @param e - Mouse or touch event containing client coordinates
     */
    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if ("touches" in e) {
        e.preventDefault();
      }

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

      if (isDragging) {
        const newX = clientX - dragStartPos.current.x;
        const newY = clientY - dragStartPos.current.y;

        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }

      if (isResizing) {
        const deltaX = clientX - resizeStartPos.current.x;
        const deltaY = clientY - resizeStartPos.current.y;

        let newWidth = resizeStartSize.current.width;
        let newHeight = resizeStartSize.current.height;
        let newX = resizeStartPosition.current.x;
        let newY = resizeStartPosition.current.y;

        if (resizeDirection.includes("e")) {
          newWidth = resizeStartSize.current.width + deltaX;
        }
        if (resizeDirection.includes("w")) {
          newWidth = resizeStartSize.current.width - deltaX;
          newX = resizeStartPosition.current.x + deltaX;
        }
        if (resizeDirection.includes("s")) {
          newHeight = resizeStartSize.current.height + deltaY;
        }
        if (resizeDirection.includes("n")) {
          newHeight = resizeStartSize.current.height - deltaY;
          newY = resizeStartPosition.current.y + deltaY;
        }

        newWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
        newHeight = Math.max(minHeight, Math.min(newHeight, maxHeight));

        if (resizeDirection.includes("w") && newWidth === minWidth) {
          newX =
            resizeStartPosition.current.x +
            (resizeStartSize.current.width - minWidth);
        }
        if (resizeDirection.includes("n") && newHeight === minHeight) {
          newY =
            resizeStartPosition.current.y +
            (resizeStartSize.current.height - minHeight);
        }

        const newSize = { width: newWidth, height: newHeight };

        setSize(newSize);
        if (resizeDirection.includes("w") || resizeDirection.includes("n")) {
          setPosition({ x: newX, y: newY });
        }

        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }

        resizeTimeoutRef.current = setTimeout(() => {
          onSizeChange?.(newSize);
        }, 300);
      }
    };

    /**
     * Handles mouse and touch up events to end dragging or resizing operations.
     * Resets all drag and resize state flags and clears the resize direction.
     */
    const handleMouseUp = () => {
      if (isResizing) {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        onSizeChange?.(size);
      }
      setIsDragging(false);
      setIsResizing(false);
      setResizeDirection("");
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleMouseMove as EventListener, {
        passive: false,
      });
      document.addEventListener("touchend", handleMouseUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = isDragging
        ? "move"
        : getCursor(resizeDirection);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener(
        "touchmove",
        handleMouseMove as EventListener,
      );
      document.removeEventListener("touchend", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [
    isDragging,
    isResizing,
    position,
    size,
    resizeDirection,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    onSizeChange,
  ]);

  const getCursor = (direction: string): string => {
    if (direction === "n" || direction === "s") return "ns-resize";
    if (direction === "e" || direction === "w") return "ew-resize";
    if (direction === "ne" || direction === "sw") return "nesw-resize";
    if (direction === "nw" || direction === "se") return "nwse-resize";
    return "default";
  };

  /**
   * Toggles window minimization state.
   * When minimizing, stores current size/position and moves window to top-left corner.
   * When restoring, returns window to previous size and position.
   */
  const handleMinimize = () => {
    if (!isMinimized) {
      setPreviousHeight(size.height);
      setPreviousWidth(size.width);
      setPreviousPosition(position);
      setPosition({
        x: 0,
        y: 0,
      });
      setSize({
        width: 300,
        height: size.height,
      });
      setIsMinimized(true);
    } else {
      setSize({ width: previousWidth, height: previousHeight });
      setPosition(previousPosition);
      setIsMinimized(false);
    }
  };

  /**
   * Toggles window maximization state.
   * If minimized, first restores then maximizes. If already maximized, restores to initial size.
   * Otherwise, maximizes to fill the viewport with margin.
   */
  const handleMaximize = () => {
    if (isMinimized) {
      setSize((prev) => ({ ...prev, height: previousHeight }));
      setPosition(previousPosition);
      setIsMinimized(false);
      setTimeout(() => {
        const newSize = { width: maxWidth, height: maxHeight };
        setSize(newSize);
        setPosition({ x: 20, y: 20 });
        onSizeChange?.(newSize);
      }, 0);
    } else if (size.width === maxWidth && size.height === maxHeight) {
      const newSize = { width: initialWidth, height: initialHeight };
      setSize(newSize);
      setPosition({
        x: (window.innerWidth - initialWidth) / 2,
        y: (window.innerHeight - initialHeight) / 2,
      });
      onSizeChange?.(newSize);
    } else {
      const newSize = { width: maxWidth, height: maxHeight };
      setSize(newSize);
      setPosition({ x: 20, y: 20 });
      onSizeChange?.(newSize);
    }
  };

  if (!open) return null;

  return (
    <WindowOverlay style={{ zIndex }}>
      <WindowContainer
        ref={windowRef}
        style={{
          left: position.x,
          top: position.y,
          width: size.width,
          height: isMinimized ? "auto" : size.height,
        }}
        onWheel={(e) => e.stopPropagation()}
        onScroll={(e) => e.stopPropagation()}
        onClick={onClick}
        onMouseDown={(e) => {
          onClick?.();
          e.stopPropagation();
        }}
      >
        <WindowHeader
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          style={{ touchAction: "none" }}
        >
          <Stack direction="row" alignItems="center">
            <DragIndicatorIcon sx={{ fontSize: 18, color: "#666", mr: 1 }} />
            <WindowTitle variant="subtitle1">{title}</WindowTitle>
            {titleTooltip && (
              <Tooltip
                title={titleTooltip}
                arrow
                placement="bottom-start"
                enterDelay={300}
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 500,
                      maxHeight: "70vh",
                      overflowY: "auto",
                      whiteSpace: "pre-line",
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                      backgroundColor: "rgba(0, 0, 0, 0.92)",
                      padding: "16px 20px",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "rgba(255, 128, 56, 0.8)",
                        borderRadius: "4px",
                        "&:hover": {
                          background: "rgba(255, 128, 56, 1)",
                        },
                      },
                    },
                  },
                }}
              >
                <InfoIcon
                  sx={{
                    fontSize: 15,
                    color: "#9ca3afab",
                    cursor: "help",
                    ml: 0.5,
                  }}
                />
              </Tooltip>
            )}
          </Stack>

          <WindowControls className="window-controls">
            <IconButton
              size="small"
              onClick={handleMinimize}
              sx={{ padding: "4px" }}
            >
              <MinimizeIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {!isMinimized && (
              <IconButton
                size="small"
                onClick={handleMaximize}
                sx={{ padding: "4px" }}
              >
                <CropSquareIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <IconButton size="small" onClick={onClose} sx={{ padding: "4px" }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </WindowControls>
        </WindowHeader>

        <WindowContent style={{ display: isMinimized ? "none" : "block" }}>
          {children}
        </WindowContent>

        {!isMinimized && (
          <>
            <ResizeHandle
              direction="n"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                handleResizeStart(e, "n")
              }
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
                handleResizeStart(e, "n")
              }
            />
            <ResizeHandle
              direction="s"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                handleResizeStart(e, "s")
              }
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
                handleResizeStart(e, "s")
              }
            />
            <ResizeHandle
              direction="e"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                handleResizeStart(e, "e")
              }
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
                handleResizeStart(e, "e")
              }
            />
            <ResizeHandle
              direction="w"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                handleResizeStart(e, "w")
              }
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
                handleResizeStart(e, "w")
              }
            />
            <ResizeHandle
              direction="ne"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                handleResizeStart(e, "ne")
              }
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
                handleResizeStart(e, "ne")
              }
            />
            <ResizeHandle
              direction="nw"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                handleResizeStart(e, "nw")
              }
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
                handleResizeStart(e, "nw")
              }
            />
            <ResizeHandle
              direction="se"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                handleResizeStart(e, "se")
              }
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
                handleResizeStart(e, "se")
              }
            />
            <ResizeHandle
              direction="sw"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                handleResizeStart(e, "sw")
              }
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
                handleResizeStart(e, "sw")
              }
            />
          </>
        )}
      </WindowContainer>
    </WindowOverlay>
  );
};

export default ResizableDraggableWindow;
